import RequestService from '@/services/v3/request.service'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { has_feature, PARTIAL_ORDERS } from '@/helpers/features'
import {
    is_partial_order_status,
    is_rule_rejection,
    order_error_message,
    status_name_for,
} from '@/helpers/request-status'

export const KEEP_OPEN_LABEL =
    'Manter a cotação aberta para os vendedores continuarem digitando os produtos ainda não pedidos'

/**
 * Pedido parcial (CLUB-1443, recurso `requests.partial_orders`).
 *
 * Com o recurso, todo "Realizar Pedido" / "Gerar pedidos" do comprador passa
 * por aqui em vez do `realizarpedido` do legado: a confirmação ganha a caixa
 * "manter a cotação aberta" e o pedido vai para
 * POST /v2/clients/requests/{id}/partial-orders, que só pede as linhas
 * vencedoras ainda não pedidas e sempre abre pedidos novos — o legado recusa um
 * segundo pedido para a mesma cotação/loja/vendedor, então rodadas seguintes só
 * funcionam por este caminho, marcada ou não a caixa.
 *
 * Sem o recurso, nada aqui é chamado: cada tela mantém o fluxo antigo.
 *
 * O endpoint não recebe observação nem altera entrega: a observação do
 * pré-pedido (que o legado mandava como `obs`) não chega ao pedido parcial.
 */
export const partial_order_mixin = {
    data() {
        return {
            partial_order_svc: new RequestService(),
            // cot_status da cotação aberta na tela, lido do kyojin. O
            // getcotacoes do legado não lista nem nomeia o 16.
            partial_request_status: null,
        }
    },
    methods: {
        partialOrdersEnabled() {
            return has_feature(PARTIAL_ORDERS)
        },

        /** A cotação está em PEDIDO PARCIAL (16) e a conta tem o recurso. */
        isPartialOrderRequest() {
            return this.partialOrdersEnabled() && is_partial_order_status(this.partial_request_status)
        },

        /**
         * Lê o status da cotação no monitor do kyojin. Só com o recurso: sem
         * ele nenhuma tela pergunta nada novo ao backend.
         */
        load_partial_request_status(request_id) {
            if (!this.partialOrdersEnabled() || !request_id) return Promise.resolve(null)

            return this.partial_order_svc
                .monitor_show(request_id)
                .then((response) => {
                    const row = response.data && response.data.data ? response.data.data : response.data
                    const status = row && row.status && row.status.id !== undefined ? row.status.id : null

                    this.partial_request_status = status
                    if (is_partial_order_status(status)) this.remember_request_status(request_id, status)

                    return status
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store)
                    return null
                })
        },

        /**
         * Atualiza a cotação no cache `q_statuses` (nome do status por id), que
         * congelar/reabrir das telas de vencedores leem.
         */
        remember_request_status(request_id, cot_status) {
            const name = status_name_for(cot_status)

            if (!name || !request_id) return

            try {
                const map = JSON.parse(localStorage.getItem('q_statuses')) || {}
                map[request_id] = name
                localStorage.setItem('q_statuses', JSON.stringify(map))
            } catch (_error) {
                // Cache é só conveniência: sem ele as telas releem o status.
            }
        },

        /**
         * Confirmação com a caixa "manter a cotação aberta" (desmarcada por
         * padrão). Resolve { confirmed, keep_open }.
         */
        confirm_order_round({ title, html, text, confirmButtonText } = {}) {
            return this.$swal
                .fire({
                    // title: '' deixa só o html (telas que já montam o título nele).
                    title: title === undefined ? 'Confirma realização do pedido?' : title,
                    html: html || undefined,
                    text: html ? undefined : text || 'Seu pedido será enviado ao fornecedor!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: confirmButtonText || 'Sim, realizar pedido!',
                    cancelButtonText: 'Cancelar',
                    input: 'checkbox',
                    inputValue: 0,
                    inputPlaceholder: KEEP_OPEN_LABEL,
                })
                .then((result) => ({
                    confirmed: !!result.isConfirmed,
                    keep_open: !!result.isConfirmed && Number(result.value) === 1,
                }))
        },

        /** Chama o endpoint e guarda o novo status. Rejeita com o erro do axios. */
        submit_partial_order(request_id, payload) {
            return this.partial_order_svc.partial_orders(request_id, payload).then((response) => {
                const data = response.data || {}

                if (data.cot_status !== undefined && data.cot_status !== null) {
                    this.partial_request_status = data.cot_status
                    this.remember_request_status(request_id, data.cot_status)
                }

                return data
            })
        },

        /**
         * Rodada completa: confirma, pede e avisa. `filters` é o recorte
         * ({ store_id, seller_ids } ou { keys }); sem recorte vão todas as linhas
         * vencedoras pendentes. Resolve a resposta do kyojin, ou null se o
         * comprador desistiu ou o pedido falhou.
         *
         * hooks.start roda depois da confirmação; hooks.finish sempre que
         * start rodou (sucesso ou erro).
         */
        partial_order_round(request_id, filters = {}, dialog = {}, hooks = {}) {
            return this.confirm_order_round(dialog).then(({ confirmed, keep_open }) => {
                if (!confirmed) {
                    if (hooks.cancel) hooks.cancel()
                    return null
                }

                if (hooks.start) hooks.start()

                return this.submit_partial_order(request_id, { ...filters, keep_open })
                    .then((data) => {
                        this.notify_partial_order_done(data)
                        return data
                    })
                    .catch((error) => {
                        this.notify_partial_order_failed(error)
                        return null
                    })
                    .finally(() => {
                        if (hooks.finish) hooks.finish()
                    })
            })
        },

        notify_partial_order_done(data) {
            const total = Array.isArray(data && data.orders) ? data.orders.length : 0
            const head = total ? `${total} pedido(s) gerado(s) com sucesso.` : 'Pedido realizado com sucesso.'
            const tail = is_partial_order_status(data && data.cot_status)
                ? 'A cotação continua aberta para os vendedores digitarem os produtos ainda não pedidos.'
                : 'A digitação da cotação foi encerrada.'

            this.$swal.fire({ title: 'Pedido realizado!', text: `${head} ${tail}`, icon: 'success' })
        },

        notify_partial_order_failed(error) {
            const rule = is_rule_rejection(error)

            if (!rule) ErrorHandlerService.handle(error, this.$store)

            this.$swal.fire({
                title: rule ? 'Atenção' : 'Erro!',
                text: order_error_message(error),
                icon: rule ? 'warning' : 'error',
            })
        },

        /**
         * "Encerrar digitação" de uma cotação em PEDIDO PARCIAL: ela vira
         * PEDIDO GERADO (4) sem gerar pedido. Resolve true se encerrou.
         */
        close_partial_typing(request_id) {
            return this.$swal
                .fire({
                    title: 'Encerrar digitação?',
                    text: 'Os vendedores não poderão mais digitar nesta cotação. Os pedidos já gerados continuam valendo.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Sim, encerrar digitação',
                    cancelButtonText: 'Cancelar',
                })
                .then((result) => {
                    if (!result.isConfirmed) return false

                    return this.partial_order_svc
                        .close_partial_orders(request_id)
                        .then((response) => {
                            const status = response.data ? response.data.cot_status : null

                            this.partial_request_status = status
                            this.remember_request_status(request_id, status)
                            this.$swal.fire({
                                title: 'Digitação encerrada!',
                                text: 'A cotação foi fechada com os pedidos já gerados.',
                                icon: 'success',
                            })

                            return true
                        })
                        .catch((error) => {
                            this.notify_partial_order_failed(error)
                            return false
                        })
                })
        },
    },
}
